import { useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import Select from "react-select";
import ImageCard from "../../components/ImageCard";
import Loader from "../../components/Loader";
import { IntlShape, useIntl } from "react-intl";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Category, MatchListItem } from "../../types";
import { useGlobalData } from "../../context/GlobalContext";
import { uploadToPresignedUrl } from "../../common/common.image.upload";
import { useFileStore } from "../../stores/fileStore";
import { toast } from "react-toastify";
import GridAddButton from "../../components/GridAddButton";
import GridPhotoItem from "../../components/GridPhotoItem";
import { useSubmitReport } from "../../hooks/useSubmitReport";
import { useAuth } from "../../context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { AIRPORT, BRAND_COLOR, DEFAULT_STORAGE_LOCATION, PLANE } from "../../constants";
import { useGetCategoryDimensionsByCategoryId } from "../../hooks/useGetCategoryDimensionsByCategoryId";
import { Dimensions } from "../../components/Dimensions";
import dayjs from "dayjs";

export const getValidationSchema = (intl?: IntlShape, isAirportSelected = true, chooseCategoryManually = false) => {
	const requiredMessage = intl ? intl.formatMessage({ id: "required", defaultMessage: "Required" }) : "Required";

	const baseFields = {
		storageLocation: z.string().min(1, requiredMessage),
		publicDescription: z.string().min(1, requiredMessage),
		internalDescription: z.string().min(1, requiredMessage),
		dimensions: z
			.record(z.string()) // dynamic keys with string values
			.optional(),
		found_date: z.string(),
	};

	const categoryFields = chooseCategoryManually
		? {
				category: z.string().min(1, requiredMessage),
				subcategory: z.string().min(1, requiredMessage),
			}
		: {};

	const airportFields = {
		...baseFields,
		place: z.string().min(1, requiredMessage),
		airline: z.string().optional(),
		flightNumber: z.string().optional(),
		...categoryFields,
	};

	const nonAirportFields = {
		...baseFields,
		place: z.string().optional(),
		airline: z.string().min(1, requiredMessage),
		flightNumber: z.string().min(1, requiredMessage),
		flightDate: z.string().min(1, requiredMessage),
		...categoryFields,
	};
	//@ts-ignore
	return z.object(isAirportSelected ? airportFields : nonAirportFields);
};

const CreateReport = () => {
	const intl = useIntl();
	const loc = useLocation();
	const queryClient = useQueryClient();
	const { userData } = useAuth();
	const submitReportMutation = useSubmitReport();

	const navigate = useNavigate();

	const [submitting, setSubmitting] = useState(false);

	const file = useFileStore((state) => state.file);

	const [fileUrl] = [loc.state.fileUrl];

	const [selectedPlaceId, setSelectedPlaceId] = useState(AIRPORT);
	const [uploadingAdditionalImage, setUploadingAdditionalImage] = useState(false);
	const [additionalImages, setAdditionalImages] = useState<string[]>([]);
	const [chooseCategoryManually, setChooseCategoryManually] = useState(false);
	const [selectedAiCategory, setSelectedAiCategory] = useState<number | null>(null);
	const [selectedCategory, setSelectedCategory] = useState("");
	const [selectedSubCategory, setSelectedSubCategory] = useState("");

	const {
		airlines,
		categories: allCategories,
		enterprise,
		storageLocations,
		placeKindsList,
		placesList,
	} = useGlobalData();

	const isAirportSelected = selectedPlaceId == AIRPORT;

	const suggestedCategories: MatchListItem[] = useMemo(() => {
		const seen = new Set<string>();
		const matchesList: MatchListItem[] = [];

		if (loc.state.matches && loc.state.matches.length > 0) {
			loc.state.matches.forEach((match: any) => {
				const id = match.ItemCategory_ID;
				if (match.ItemCategory_fk != null && !seen.has(id)) {
					const rootCategory = allCategories?.find((c) => c.ItemCategory_ID === match.ItemCategory_fk);

					if (rootCategory) {
						seen.add(id);

						matchesList.push({
							itemCategoryId: id,
							itemCategoryFk: match.ItemCategory_fk,
							localizedValue: match.LocalizedValue,
							confidence: Number(match.confidence).toFixed(2),
							rootCategory: rootCategory?.LocalizedValue,
						});
					}
				}
			});
		}

		if (matchesList.length === 0) {
			setChooseCategoryManually(true);
		}

		return matchesList;
	}, [loc.state.matches]);

	const { categories, subcategories } = useMemo(() => {
		const categories: any = [];
		const subcategories: any = [];

		if (allCategories) {
			allCategories.forEach((item: Category) => {
				const common = {
					categoryId: item.ItemCategory_ID,
					localizedValue: item.LocalizedValue,
					sequence: item.Sequence,
				};

				if (!item.ItemCategory_fk) {
					categories.push(common);
				} else {
					subcategories.push({
						...common,
						parentCategoryId: item.ItemCategory_fk,
					});
				}
			});
		}

		return { categories, subcategories };
	}, [allCategories]);

	const schema = getValidationSchema(intl, isAirportSelected, chooseCategoryManually);

	type ReportFormData = z.infer<typeof schema>;

	const {
		control,
		register,
		handleSubmit,
		getValues,
		setValue,
		formState: { errors, defaultValues },
	} = useForm<ReportFormData>({
		//@ts-ignore
		resolver: zodResolver(schema),
		defaultValues: {
			storageLocation: DEFAULT_STORAGE_LOCATION,
			found_date: new Date().toISOString().split("T")[0],
		},
	});

	const { data: result } = useGetCategoryDimensionsByCategoryId({
		locale: userData?.user.locale,
		categoryId: selectedSubCategory || selectedAiCategory,
	});

	const dimensionsAndOptions = useMemo(() => {
		const dimensions: any = {};
		if (result) {
			result.forEach((dim) => {
				if (!dimensions[dim.CategoryDimension_ID]) {
					dimensions[dim.CategoryDimension_ID] = {
						dimensionId: dim.CategoryDimension_ID,
						localizedValue: dim.Dimension,
						isDimensionTextBox: dim.IsDimensionTextBox,
						options: [],
					};
				}
				if (dim.CategoryDimensionOption_ID && dim.DimensionOption) {
					dimensions[dim.CategoryDimension_ID].options.push({
						optionId: dim.CategoryDimensionOption_ID,
						localizedValue: dim.DimensionOption,
						isTextbox: dim.IsTextbox,
					});
				}
			});
		}
		return dimensions;
	}, [result]);

	const subcategoriesList = selectedCategory
		? subcategories.filter((a: any) => a.parentCategoryId === selectedCategory)
		: [];

	const handleSuggestedCategoryClick = (key: number) => {
		setSelectedAiCategory(key);
	};

	const handleAdditionalImageUpload = async (file?: File) => {
		try {
			const portal = userData?.user.portals.find((f) => f != 0);

			if (file && portal) {
				setUploadingAdditionalImage(true);
				const imageUrl = await uploadToPresignedUrl(file, portal);
				setAdditionalImages((prev) => [...prev, imageUrl]);
				setUploadingAdditionalImage(false);
			}
		} catch (error) {
			console.error("Error uploading additional image:", error);
			setUploadingAdditionalImage(false);
			toast.error(intl.formatMessage({ id: "image_upload_failed" }));
		}
	};

	const onSubmit = async (data: ReportFormData) => {
		try {
			const portal = userData?.user.portals.find((f) => f != 0);
			if (file && portal) {
				setSubmitting(true);

				if (!chooseCategoryManually) {
					data.subcategory = selectedAiCategory;
				}

				let dataToSubmit = null;
				const imageUrl = await uploadToPresignedUrl(file, portal);
				if (selectedPlaceId === PLANE) {
					dataToSubmit = {
						findReportChildPlaceFk: null,
						findReportPlaceFk: PLANE,
						lossObjectStorageLocationFk: data.storageLocation,
						lossObjectPublicDescription: data.publicDescription,
						lossObjectInternalDescription: data.internalDescription,
						lossObjectCategoryFk: data.subcategory,
						findReportComment: "Mobile agent",
						findReportAirlineFk: data.airline,
						findReportFlightDate: data.flightDate,
						findReportFlightNumber: data.flightNumber,
						lossObjectImages: [imageUrl, ...(additionalImages || [])],
						dimensions: data.dimensions,
						foundDate: data.found_date,
					};
				} else {
					dataToSubmit = {
						findReportChildPlaceFk: data.place,
						findReportPlaceFk: AIRPORT,
						lossObjectStorageLocationFk: data.storageLocation,
						lossObjectPublicDescription: data.publicDescription,
						lossObjectInternalDescription: data.internalDescription,
						lossObjectCategoryFk: data.subcategory,
						foundDate: data.found_date,
						findReportComment: "Mobile agent",
						lossObjectImages: [imageUrl, ...(additionalImages || [])],
						dimensions: data.dimensions,
					};
				}

				console.log("Form Data:", JSON.stringify(dataToSubmit, null, 2));
				const submitReportMutationResponse = await submitReportMutation.mutateAsync(dataToSubmit);

				// //@ts-ignore
				//  await queryClient.invalidateQueries(["getFindReports"]);
				navigate(`/backoffice/report/${submitReportMutationResponse}`, { replace: true });

				toast.success(intl.formatMessage({ id: "report_submit_success" }));

				setSubmitting(false);
			}
		} catch (error) {
			setSubmitting(false);
			toast.error(intl.formatMessage({ id: "report_submit_error" }));
			console.error("Error submitting form:", error);
		}
	};

	const handleChooseCategoryManuallyClick = () => {
		setChooseCategoryManually(true);
		setSelectedAiCategory(null);
	};

	if (submitting) {
		return (
			<div className="d-flex justify-content-center align-items-center vh-100 vw-100">
				<Loader message="Submitting your report..." />
			</div>
		);
	}

	const airlineOptions = airlines?.map((a) => ({ value: a.UID, label: a.AirlineName })) ?? [];

	const showForm = chooseCategoryManually || selectedAiCategory;

	return (
		<div className="container mt-3 mb-3">
			<div className="d-flex justify-content-between align-items-center mb-2">
				<button className="btn btn-light" onClick={() => navigate(-1)}>
					<i className="fas fa-arrow-left me-1"></i> {intl.formatMessage({ id: "back" })}
				</button>
			</div>
			<ImageCard src={fileUrl} />
			{!chooseCategoryManually ? (
				<>
					<h4 className="card-title">{intl.formatMessage({ id: "ai_guess_select_best" })}</h4>
					{/* SUGGESTED CATEGORIES LIST */}
					<ul className="list-group mt-2">
						{suggestedCategories.map(({ itemCategoryId, localizedValue, rootCategory, itemCategoryFk }) => (
							<li
								key={itemCategoryId}
								onClick={() => handleSuggestedCategoryClick(itemCategoryId)}
								className={`list-group-item d-flex justify-content-between align-items-center ${
									selectedAiCategory == itemCategoryId ? "list-group-item-success" : ""
								}`}
								style={{ cursor: "pointer" }}
							>
								<div className="d-flex align-items-center w-100">
									<div>
										{localizedValue}
										<small className="text-secondary d-block">{rootCategory}</small>
									</div>
									<div className="ms-auto">
										<span className="fw-lighter" style={{ fontSize: 10 }}>
											{itemCategoryId}
										</span>
									</div>
								</div>
							</li>
						))}
						<li
							key={"choose_category_manually"}
							onClick={handleChooseCategoryManuallyClick}
							className={`list-group-item d-flex justify-content-between align-items-center list-group-item-info text-light`}
							style={{ cursor: "pointer", backgroundColor: BRAND_COLOR }}
						>
							{intl.formatMessage({ id: "choose_category_manually" })}
						</li>
					</ul>
				</>
			) : null}
			{/* ADDITIONAL PHOTOS */}
			{showForm ? (
				<div className="row p-2">
					<GridAddButton loading={uploadingAdditionalImage} handlePhotoSelect={handleAdditionalImageUpload} />
					{additionalImages.map((src, i) => (
						<GridPhotoItem noAction={true} key={i} src={src} />
					))}
				</div>
			) : null}
			{/* REPORT FORM */}
			{showForm ? (
				<div className="container mt-3" style={{ maxWidth: 600 }}>
					<form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column gap-3">
						{chooseCategoryManually ? (
							<>
								<div>
									<label className="form-label">{intl.formatMessage({ id: "category" })}</label>
									<select
										{...register("category")}
										className="form-select"
										onChange={(e) => {
											setSelectedCategory(e.target.value);
										}}
									>
										<option value="">{intl.formatMessage({ id: "select" })}</option>
										{categories.map((c: any) => (
											<option key={c.categoryId} value={c.categoryId}>
												{c.localizedValue}
											</option>
										))}
									</select>
									{errors.category && (
										<small className="text-danger">{errors.category.message as string}</small>
									)}
								</div>
								<div>
									<label className="form-label">{intl.formatMessage({ id: "subcategory" })}</label>
									<select
										{...register("subcategory")}
										className="form-select"
										onChange={(e) => {
											if (selectedCategory != e.target.value) {
												setValue("dimensions", {});
											}

											setSelectedSubCategory(e.target.value);
										}}
									>
										<option value="">{intl.formatMessage({ id: "select" })}</option>
										{subcategoriesList.map((sc: any) => (
											<option key={sc.categoryId} value={sc.categoryId}>
												{sc.localizedValue}
											</option>
										))}
									</select>
									{errors.subcategory && (
										<small className="text-danger">{errors.subcategory.message as string}</small>
									)}
								</div>
							</>
						) : null}
						<Dimensions
							dimensionsAndOptions={Object.values(dimensionsAndOptions)}
							register={register}
							getValues={getValues}
							errors={errors}
						/>
						<div>
							<label className="form-label">
								{intl.formatMessage({
									id: "found_date",
								})}
							</label>
							<input
								type="date"
								{...register("found_date")}
								className="form-control"
								min={dayjs().add(-3, "month").format("YYYY-MM-DD")}
								max={dayjs().format("YYYY-MM-DD")}
							/>
							{errors.found_date && (
								<small className="text-danger">{errors.found_date.message as string}</small>
							)}
						</div>
						<div>
							<label className="form-label">{intl.formatMessage({ id: "airport_plane" })}</label>
							<select
								className="form-select"
								value={selectedPlaceId}
								onChange={(e) => setSelectedPlaceId(e.target.value)}
							>
								{placeKindsList?.map((p) => (
									<option key={p.LossPlace_ID} value={p.LossPlace_ID}>
										{p.LocalizedValue}
									</option>
								))}
							</select>
						</div>
						{isAirportSelected && (
							<div>
								<label className="form-label">{intl.formatMessage({ id: "place" })}</label>
								<select {...register("place")} className="form-select">
									<option value="">{intl.formatMessage({ id: "select" })}</option>
									{placesList?.map((p) => (
										<option key={p.LossPlace_ID} value={p.LossPlace_ID}>
											{p.LocalizedValue}
										</option>
									))}
								</select>
								{errors.place && (
									<small className="text-danger">{errors.place.message as string}</small>
								)}
							</div>
						)}
						{!isAirportSelected && (
							<>
								<div className="mb-3">
									<label className="form-label">{intl.formatMessage({ id: "airline" })}</label>

									<Controller
										name="airline"
										control={control}
										render={({ field }) => (
											<Select
												{...field}
												options={airlineOptions}
												placeholder={intl.formatMessage({ id: "select" })}
												isClearable
												classNamePrefix="react-select"
												styles={{
													control: (base, state) => ({
														...base,
														borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
														boxShadow: state.isFocused
															? "0 0 0 0.25rem rgba(13,110,253,.25)"
															: undefined,
														"&:hover": {
															borderColor: "#86b7fe",
														},
														minHeight: "38px",
													}),
													valueContainer: (base) => ({
														...base,
														padding: "0.375rem 0.75rem",
													}),
													singleValue: (base) => ({
														...base,
														fontSize: "1rem",
													}),
													menu: (base) => ({
														...base,
														zIndex: 9999,
													}),
												}}
												value={airlineOptions.find((opt) => opt.value === field.value) || null}
												onChange={(option) => field.onChange(option?.value || "")}
											/>
										)}
									/>

									{errors.airline && (
										<small className="text-danger">{errors.airline.message as string}</small>
									)}
								</div>
								<div>
									<label className="form-label">{intl.formatMessage({ id: "flight_number" })}</label>
									<input type="text" {...register("flightNumber")} className="form-control" />
									{errors.flightNumber && (
										<small className="text-danger">{errors.flightNumber.message as string}</small>
									)}
								</div>
								<div>
									<label className="form-label">
										{intl.formatMessage({
											id: isAirportSelected ? "date" : "flight_date",
										})}
									</label>
									<input
										type="date"
										{...register("flightDate")}
										className="form-control"
										min={dayjs().add(-3, "month").format("YYYY-MM-DD")}
										max={dayjs().format("YYYY-MM-DD")}
									/>
									{errors.flightDate && (
										<small className="text-danger">{errors.flightDate.message as string}</small>
									)}
								</div>
							</>
						)}
						<div>
							<label className="form-label">{intl.formatMessage({ id: "storage_location" })}</label>
							<select {...register("storageLocation")} className="form-select">
								<option value="">{intl.formatMessage({ id: "select" })}</option>
								{storageLocations?.map((sl) => (
									<option key={sl.StorageLocation_ID} value={sl.StorageLocation_ID}>
										{sl.LocationName}
									</option>
								))}
							</select>
							{errors.storageLocation && (
								<small className="text-danger">{errors.storageLocation.message as string}</small>
							)}
						</div>
						<div>
							<label className="form-label">{intl.formatMessage({ id: "public_description" })}</label>
							<textarea
								{...register("publicDescription")}
								className="form-control"
								rows={2}
								maxLength={1000}
							></textarea>
							{errors.publicDescription && (
								<small className="text-danger">{errors.publicDescription.message as string}</small>
							)}
						</div>
						<div>
							<label className="form-label">{intl.formatMessage({ id: "internal_description" })}</label>
							<textarea
								{...register("internalDescription")}
								className="form-control"
								rows={2}
								maxLength={1000}
							></textarea>
							{errors.internalDescription && (
								<small className="text-danger">{errors.internalDescription.message as string}</small>
							)}
						</div>
						<button disabled={uploadingAdditionalImage} type="submit" className="btn btn-primary mt-2">
							{intl.formatMessage({ id: "submit" })}
						</button>
					</form>
				</div>
			) : null}
		</div>
	);
};
export default CreateReport;
